import * as Yup from "yup";
import { AsyncSelectValue } from "@/components/AsyncSelect";
import { CcmConsentFormValues } from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-types";
import { PatientConsentFormValues } from "@/pages/app/provider/consent-forms/patient-consent/patient-consent-types";
import { Option } from "@/constants/common";
import { INITIAL_PATIENT_INSURANCE } from "@/constants/provider/patient/add-new-patient/add-new-patient-form";
import {
  PatientInsurance,
  PatientInsuranceOrderOfBenefitsEnum,
} from "@/services";

interface Allergies {
  none: boolean;
  [key: string]: boolean;
}

interface SocialHistory {
  alcohol: boolean;
  smoke: boolean;
  caffeine: boolean;
  sexuallyActive: boolean;
  stdCheck: boolean;
  [key: string]: boolean;
}

interface MedicalHistory {
  [key: string]: boolean;
}

interface FamilyHistoryItem {
  problem: string;
  relative: string;
  age: number;
}

interface SurgicalHistoryItem {
  type: string;
  date: string;
  doctor: string;
  location: string;
}

interface MedicationItem {
  name: string;
  dosage: string;
  frequency: string;
  prescribedBy: string;
}

interface InsuranceInfo {
  insuranceProvider: string;
  policyNumber: string;
  groupNumber: string;
  subscriberName: string;
  subscriberDOB: string;
  subscriberSSN: string;
  relationshipToPatient: string;
}

/** One insurance policy — the payer and plan stay async-select options. */
export type IntakeInsurance = Omit<
  PatientInsurance,
  "insurancePayer" | "planId" | "planName"
> & {
  insurancePayer?: { payerId?: AsyncSelectValue };
  planId?: AsyncSelectValue;
  planName?: AsyncSelectValue | string;
};

/** Per BA the patient may declare at most three policies on the intake form. */
export const MAX_INTAKE_INSURANCE_COUNT = 3;

export const INSURANCE_ANSWER = {
  YES: "yes",
  NO: "no",
} as const;

export const ORDER_OF_BENEFITS_OPTIONS: Option[] = [
  { value: PatientInsuranceOrderOfBenefitsEnum.Primary, label: "Primary" },
  { value: PatientInsuranceOrderOfBenefitsEnum.Secondary, label: "Secondary" },
  { value: PatientInsuranceOrderOfBenefitsEnum.Ternary, label: "Tertiary" },
  { value: PatientInsuranceOrderOfBenefitsEnum.Other, label: "Other" },
];

/** Same record the patient chart saves, so the policy is reusable there. */
export const INITIAL_INTAKE_INSURANCE: IntakeInsurance = {
  ...INITIAL_PATIENT_INSURANCE,
  insurancePayer: undefined,
  planId: undefined,
  planName: "",
};

export interface IntakeFormValues {
  // sign
  hippaConsentSign?: string;
  hippaConsentDate?: string;
  physicianPatientArbitrationAgreementSign?: string;
  physicianPatientArbitrationAgreementDate?: string;
  medicalServicesAgreementSign?: string;
  medicalServicesAgreementDate?: string;
  // Basic Details
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: string;
  phoneNumber: string;
  email: string;
  address: any;
  city: string;
  state: string;
  zipCode: string;

  // Insurance Information
  /** Answer to "Do you have Insurance?"; the policies below apply only when true. */
  hasInsurance: boolean;
  primaryInsurance: boolean;
  secondaryInsurance: boolean;
  primary: InsuranceInfo;
  secondary: InsuranceInfo;
  patientInsurances: IntakeInsurance[];

  // Medical History
  allergies: Allergies;
  allergiesNote: string;
  familyHistory: FamilyHistoryItem[];
  socialHistory: SocialHistory;
  socialHistoryNote: string;
  surgicalHistory: SurgicalHistoryItem[];
  medicalHistory: MedicalHistory;
  medicalHistoryNote: string;
  medications: MedicationItem[];

  // Consent and Agreements
  hippaConsent: boolean;
  medicalServicesAgreement: boolean;
  physicianPatientArbitrationAgreement: boolean;
  consent: boolean;

  paymentPolicyDate?: Date;

  // Individual Psychotherapy
  individualPsychotherapy: {
    primaryCarePhysicianServiceFocused: string;
    primaryDiagnosis: string;
    secondaryDiagnosis: string;
    primaryCarePhysician: string;
    currentMedications: string;
    allergies: string;
    relevantMedicalHistory: string;
    previousMentalHealthTreatment: string;
    psychiatricMedications: string;
    suicidalThoughtsHistory: string;
    substanceUseHistory: string;
    traumaHistory: string;
    currentCopingMechanisms: string;
    chiefConcern: string;
    onsetDate: string;
    areasOfConcern: { [key: string]: boolean };
    previousTreatmentTherapy: string;
    treatmentGoals: string;
    birthHistory: string;
    developmentalMilestones: string;
    familyMedicalMentalHealthHistory: string;
    currentSupportSystem: string;
    educationLevel: string;
    occupation: string;
    employmentStatus: string;
    referralSource: string;
    insuranceProvider: string;
    policyMemberId: string;
  };
  "Telehealth Consent"?: CcmConsentFormValues;
  "Authorization/PHI Release Consent"?: PatientConsentFormValues;
  primerMD_ccm_Form?: CcmConsentFormValues;
  primerMD_patient_consent_Form?: PatientConsentFormValues;
}

export const ALLERGIES = [
  { label: "NONE/Known Allergies", value: "none" },
  { label: "Adhesive Tape", value: "adhesiveTape" },
  { label: "Anesthesia", value: "anesthesia" },
  { label: "Aspirin", value: "aspirin" },
  { label: "Codeine", value: "codeine" },
  { label: "Dairy Products", value: "dairyProducts" },
  { label: "Iodine/Shellfish/Contrast", value: "iodine" },
  { label: "Latex", value: "latex" },
  { label: "Morphine", value: "morphine" },
  { label: "Penicillin", value: "penicillin" },
];

export const FAMILY_HISTORY_CONDITIONS = [
  { value: "Anesthesia Problems", label: "Anesthesia Problems" },
  { value: "Arthritis", label: "Arthritis" },
  { value: "Cancer", label: "Cancer" },
  { value: "Diabetes", label: "Diabetes" },
  { value: "Heart Problems", label: "Heart Problems" },
  { value: "Hypertension", label: "Hypertension" },
  { value: "Stroke", label: "Stroke" },
  { value: "Thyroid Disorder", label: "Thyroid Disorder" },
];

export const SOCIAL_HISTORY_QUESTIONS = [
  { label: "Do you drink alcohol?", name: "alcohol" },
  { label: "Do you smoke?", name: "smoke" },
  { label: "Do you drink caffeine?", name: "caffeine" },
  { label: "Are you sexually active?", name: "sexuallyActive" },
  { label: "Do you wish to be checked for STDs?", name: "stdCheck" },
];

export const MEDICAL_CONDITIONS = [
  { value: "noneOfTheProblemsListed", label: "None of the Problems Listed" },
  { value: "allergies", label: "Allergies" },
  { value: "anemia", label: "Anemia" },
  { value: "arthritisConditions", label: "Arthritis Conditions" },
  { value: "asthma", label: "Asthma" },
  { value: "arterialFibrillation", label: "Arterial Fibrillation" },
  { value: "bleedingProblems", label: "Bleeding Problems" },
  { value: "bph", label: "BPH" },
  { value: "cadCoronaryArteryDisease", label: "CAD (Coronary Artery Disease)" },
  { value: "cancer", label: "Cancer" },
  { value: "cardiacArrest", label: "Cardiac Arrest" },
  { value: "celiacDisease", label: "Celiac Disease" },
  { value: "chestPain", label: "Chest Pain" },
  { value: "congestiveHeartFailure", label: "Congestive Heart Failure" },
  { value: "chronicFatigueSyndrome", label: "Chronic Fatigue Syndrome" },
  { value: "depression", label: "Depression" },
  { value: "diabetes", label: "Diabetes" },
  { value: "drugAlcoholAbuse", label: "Drug/Alcohol Abuse" },
  { value: "erectileDysfunction", label: "Erectile Dysfunction" },
  { value: "fibromyalgia", label: "Fibromyalgia" },
  { value: "gerd", label: "GERD" },
  { value: "heartDisease", label: "Heart Disease" },
  { value: "hyperinsulinemia", label: "Hyperinsulinemia" },
  { value: "hyperlipidemia", label: "Hyperlipidemia" },
  { value: "hypertension", label: "Hypertension" },
  { value: "hypogonadismMale", label: "Hypogonadism (Male)" },
  { value: "hypothyroidism", label: "Hypothyroidism" },
  { value: "infectionProblems", label: "Infection Problems" },
  { value: "insomnia", label: "Insomnia" },
  { value: "irritableBowelSyndrome", label: "Irritable Bowel Syndrome" },
  { value: "kidneyProblems", label: "Kidney Problems" },
  { value: "menopause", label: "Menopause" },
  { value: "migrainesHeadaches", label: "Migraines/Headaches" },
  { value: "neuropathy", label: "Neuropathy" },
  { value: "onychomycosis", label: "Onychomycosis" },
  { value: "organInjury", label: "Organ Injury" },
  { value: "osteoporosis", label: "Osteoporosis" },
  { value: "pulmonaryEmbolism", label: "Pulmonary Embolism" },
  { value: "seizureDisorder", label: "Seizure Disorder" },
  { value: "shortnessOfBreath", label: "Shortness of Breath" },
  { value: "sinusConditions", label: "Sinus Conditions" },
  { value: "stroke", label: "Stroke" },
  { value: "syndromeX", label: "Syndrome X" },
  { value: "tremors", label: "Tremors" },
  { value: "wheatAllergy", label: "Wheat Allergy" },
];

export const MEDICAL_CONDITIONS_STRING = [
  {
    value: "None of the Problems Listed",
    label: "None of the Problems Listed",
  },
  { value: "Allergies", label: "Allergies" },
  { value: "Anemia", label: "Anemia" },
  { value: "Arthritis Conditions", label: "Arthritis Conditions" },
  { value: "Asthma", label: "Asthma" },
  { value: "Arterial Fibrillation", label: "Arterial Fibrillation" },
  { value: "Bleeding Problems", label: "Bleeding Problems" },
  { value: "BPH", label: "BPH" },
  {
    value: "CAD (Coronary Artery Disease)",
    label: "CAD (Coronary Artery Disease)",
  },
  { value: "Cancer", label: "Cancer" },
  { value: "Cardiac Arrest", label: "Cardiac Arrest" },
  { value: "Celiac Disease", label: "Celiac Disease" },
  { value: "Chest Pain", label: "Chest Pain" },
  { value: "Congestive Heart Failure", label: "Congestive Heart Failure" },
  { value: "Chronic Fatigue Syndrome", label: "Chronic Fatigue Syndrome" },
  { value: "Depression", label: "Depression" },
  { value: "Diabetes", label: "Diabetes" },
  { value: "Drug/Alcohol Abuse", label: "Drug/Alcohol Abuse" },
  { value: "Erectile Dysfunction", label: "Erectile Dysfunction" },
  { value: "Fibromyalgia", label: "Fibromyalgia" },
  { value: "GERD", label: "GERD" },
  { value: "Heart Disease", label: "Heart Disease" },
  { value: "Hyperinsulinemia", label: "Hyperinsulinemia" },
  { value: "Hyperlipidemia", label: "Hyperlipidemia" },
  { value: "Hypertension", label: "Hypertension" },
  { value: "Hypogonadism (Male)", label: "Hypogonadism (Male)" },
  { value: "Hypothyroidism", label: "Hypothyroidism" },
  { value: "Infection Problems", label: "Infection Problems" },
  { value: "Insomnia", label: "Insomnia" },
  { value: "Irritable Bowel Syndrome", label: "Irritable Bowel Syndrome" },
  { value: "Kidney Problems", label: "Kidney Problems" },
  { value: "Menopause", label: "Menopause" },
  { value: "Migraines/Headaches", label: "Migraines/Headaches" },
  { value: "Neuropathy", label: "Neuropathy" },
  { value: "Onychomycosis", label: "Onychomycosis" },
  { value: "Organ Injury", label: "Organ Injury" },
  { value: "Osteoporosis", label: "Osteoporosis" },
  { value: "Pulmonary Embolism", label: "Pulmonary Embolism" },
  { value: "Seizure Disorder", label: "Seizure Disorder" },
  { value: "Shortness of Breath", label: "Shortness of Breath" },
  { value: "Sinus Conditions", label: "Sinus Conditions" },
  { value: "Stroke", label: "Stroke" },
  { value: "Syndrome X", label: "Syndrome X" },
  { value: "Tremors", label: "Tremors" },
  { value: "Wheat Allergy", label: "Wheat Allergy" },
];

export const DOSAGE_OPTIONS = [
  { label: "1 mg", value: "1mg" },
  { label: "2 mg", value: "2mg" },
  { label: "5 mg", value: "5mg" },
  { label: "10 mg", value: "10mg" },
  { label: "20 mg", value: "20mg" },
  { label: "25 mg", value: "25mg" },
  { label: "50 mg", value: "50mg" },
  { label: "100 mg", value: "100mg" },
  { label: "250 mg", value: "250mg" },
  { label: "500 mg", value: "500mg" },
  { label: "1000 mg", value: "1000mg" },
];

export const FREQUENCY_OPTIONS = [
  { label: "Once daily", value: "onceDaily" },
  { label: "Twice daily", value: "twiceDaily" },
  { label: "Three times daily", value: "threeTimesDaily" },
  { label: "Four times daily", value: "fourTimesDaily" },
  { label: "As needed", value: "asNeeded" },
  { label: "Every 4 hours", value: "every4Hours" },
  { label: "Every 6 hours", value: "every6Hours" },
  { label: "Every 8 hours", value: "every8Hours" },
  { label: "Every 12 hours", value: "every12Hours" },
  { label: "Every 24 hours", value: "every24Hours" },
];

export const validationSchema = (activeSections: string[] = []) => {
  return Yup.object().shape({
    // Basic Details
    firstName: activeSections.includes("demographics")
      ? Yup.string().required("First name is required")
      : Yup.string(),
    lastName: activeSections.includes("demographics")
      ? Yup.string().required("Last name is required")
      : Yup.string(),
    // dateOfBirth: activeSections.includes("demographics")
    //   ? Yup.string().required("Date of birth is required")
    //   : Yup.string(),
    // gender: activeSections.includes("demographics")
    //   ? Yup.string().required("Gender is required")
    //   : Yup.string(),
    // phoneNumber: activeSections.includes("demographics")
    //   ? Yup.string().required("Phone number is required")
    //   : Yup.string(),
    // email: activeSections.includes("demographics")
    //   ? Yup.string().email("Invalid email").required("Email is required")
    //   : Yup.string().email("Invalid email"),
    // address: activeSections.includes("demographics")
    //   ? Yup.string().required("Address is required")
    //   : Yup.string(),
    // city: activeSections.includes("demographics")
    //   ? Yup.string().required("City is required")
    //   : Yup.string(),
    // state: activeSections.includes("demographics")
    //   ? Yup.string().required("State is required")
    //   : Yup.string(),
    // zipCode: activeSections.includes("demographics")
    //   ? Yup.string().required("Zip code is required")
    //   : Yup.string(),

    // // Insurance Information
    // primaryInsurance: Yup.boolean(),
    // secondaryInsurance: Yup.boolean(),
    // primary: Yup.object().when("primaryInsurance", {
    //   is: true,
    //   then: (schema) =>
    //     activeSections.includes("insurance")
    //       ? schema.shape({
    //           insuranceProvider: Yup.string().required(
    //             "Insurance provider is required"
    //           ),
    //           policyNumber: Yup.string().required("Policy number is required"),
    //           groupNumber: Yup.string().required("Group number is required"),
    //           subscriberName: Yup.string().required(
    //             "Subscriber name is required"
    //           ),
    //           subscriberDOB: Yup.string().required(
    //             "Subscriber DOB is required"
    //           ),
    //           subscriberSSN: Yup.string().required(
    //             "Subscriber SSN is required"
    //           ),
    //           relationshipToPatient: Yup.string().required(
    //             "Relationship to patient is required"
    //           ),
    //         })
    //       : schema,
    // }),
    // secondary: Yup.object().when("secondaryInsurance", {
    //   is: true,
    //   then: (schema) =>
    //     activeSections.includes("insurance")
    //       ? schema.shape({
    //           insuranceProvider: Yup.string().required(
    //             "Insurance provider is required"
    //           ),
    //           policyNumber: Yup.string().required("Policy number is required"),
    //           groupNumber: Yup.string().required("Group number is required"),
    //           subscriberName: Yup.string().required(
    //             "Subscriber name is required"
    //           ),
    //           subscriberDOB: Yup.string().required(
    //             "Subscriber DOB is required"
    //           ),
    //           subscriberSSN: Yup.string().required(
    //             "Subscriber SSN is required"
    //           ),
    //           relationshipToPatient: Yup.string().required(
    //             "Relationship to patient is required"
    //           ),
    //         })
    //       : schema,
    // }),

    // Medical History
    // allergies: activeSections.includes("patientmedicalhistory")
    //   ? Yup.object().required("Allergies information is required")
    //   : Yup.object(),
    // allergiesNote: Yup.string(),
    // familyHistory: activeSections.includes("patientmedicalhistory")
    //   ? Yup.array().of(
    //       Yup.object().shape({
    //         problem: Yup.string().required("Problem is required"),
    //         relative: Yup.string().required("Relative is required"),
    //         age: Yup.number().required("Age is required"),
    //       })
    //     )
    //   : Yup.array(),
    // socialHistory: activeSections.includes("patientmedicalhistory")
    //   ? Yup.object().required("Social history is required")
    //   : Yup.object(),
    // socialHistoryNote: Yup.string(),
    // surgicalHistory: activeSections.includes("patientmedicalhistory")
    //   ? Yup.array().of(
    //       Yup.object().shape({
    //         type: Yup.string().required("Surgery type is required"),
    //         date: Yup.string().required("Surgery date is required"),
    //         doctor: Yup.string().required("Doctor name is required"),
    //         location: Yup.string().required("Location is required"),
    //       })
    //     )
    //   : Yup.array(),
    // medicalHistory: activeSections.includes("patientmedicalhistory")
    //   ? Yup.object().required("Medical history is required")
    //   : Yup.object(),
    // medicalHistoryNote: Yup.string(),
    // medications: activeSections.includes("patientmedicalhistory")
    //   ? Yup.array().of(
    //       Yup.object().shape({
    //         name: Yup.string().required("Medication name is required"),
    //         dosage: Yup.string().required("Dosage is required"),
    //         frequency: Yup.string().required("Frequency is required"),
    //         prescribedBy: Yup.string().required("Prescribed by is required"),
    //       })
    //     )
    //   : Yup.array(),

    // Consent and Agreements
    // HIPAA consent validation commented out (ECRH-4705) — unblock final Submit.
    // hippaConsentSign: activeSections.includes("HIPAAConsent")
    //   ? Yup.string().required("HIPAA consent signature is required")
    //   : Yup.string(),

    // hippaConsent:
    //   activeSections.includes("HIPAAConsent") ||
    //   activeSections.includes(" HIPAA Consent")
    //     ? Yup.boolean().when(["hippaConsentSign"], {
    //         is: (sign?: string) => {
    //           return !sign;
    //         },
    //         then: (schema) => {
    //           return schema
    //             .test(
    //               "hippaConsent",
    //               "You must Sign the HIPAA consent",
    //               function () {
    //                 return this.parent.hippaConsentSign;
    //               }
    //             )
    //             .oneOf([true], "You must accept the HIPAA consent")
    //             .required("HIPAA consent is required");
    //         },
    //         otherwise: (schema) =>
    //           schema
    //             .oneOf([true], "You must accept the HIPAA consent")
    //             .required("HIPAA consent is required"),
    //       })
    //     : Yup.boolean(),

    // medicalServicesAgreement:
    //   activeSections.includes("medicalServicesAgreement") ||
    //   activeSections.includes("Medical Services Agreement")
    //     ? Yup.boolean().when(["medicalServicesAgreementSign"], {
    //         is: (sign?: string) => {
    //           return !sign;
    //         },
    //         then: (schema) => {
    //           return schema
    //             .test(
    //               "medicalServicesAgreement",
    //               "You must Sign the Medical Services Agreement",
    //               function () {
    //                 return this.parent.medicalServicesAgreementSign;
    //               }
    //             )
    //             .oneOf([true], "You must accept the medical services agreement")
    //             .required("Medical services agreement is required");
    //         },
    //         otherwise: (schema) =>
    //           schema
    //             .oneOf([true], "You must accept the medical services agreement")
    //             .required("Medical services agreement is required"),
    //       })
    //     : Yup.boolean(),
    medicalServicesAgreementSign:
      activeSections.includes("medicalServicesAgreement") ||
      activeSections.includes("Medical Services Agreement")
        ? Yup.string().required(
            "Medical services agreement signature is required"
          )
        : Yup.string(),

    // physicianPatientArbitrationAgreement:
    //   activeSections.includes("physicianPatientArbitrationAgreement") ||
    //   activeSections.includes(" Physician Patient Arbitration Agreement")
    //     ? Yup.boolean().when(["physicianPatientArbitrationAgreementSign"], {
    //         is: (sign?: string) => {
    //           return !sign;
    //         },
    //         then: (schema) => {
    //           return schema
    //             .test(
    //               "physicianPatientArbitrationAgreement",
    //               "You must Sign the Physician-Patient Arbitration Agreement",
    //               function () {
    //                 return this.parent.physicianPatientArbitrationAgreementSign;
    //               }
    //             )
    //             .oneOf(
    //               [true],
    //               "You must accept the physician-patient arbitration agreement"
    //             )
    //             .required(
    //               "Physician-patient arbitration agreement is required"
    //             );
    //         },
    //         otherwise: (schema) =>
    //           schema
    //             .oneOf(
    //               [true],
    //               "You must accept the physician-patient arbitration agreement"
    //             )
    //             .required(
    //               "Physician-patient arbitration agreement is required"
    //             ),
    //       })
    //     : Yup.boolean(),
    physicianPatientArbitrationAgreement: Yup.boolean(),
    physicianPatientArbitrationAgreementSign: activeSections.includes(
      "physicianPatientArbitrationAgreement"
    )
      ? Yup.string().required(
          "Physician-patient arbitration agreement signature is required"
        )
      : Yup.string(),

    // consent: activeSections.includes("consent")
    //   ? Yup.boolean()
    //       .oneOf([true], "You must accept the consent and privacy policy")
    //       .required("Consent is required")
    // : Yup.boolean(),
  });
};

export const initialValues: IntakeFormValues = {
  // sign
  hippaConsentSign: undefined,
  hippaConsentDate: undefined,
  physicianPatientArbitrationAgreementSign: undefined,
  physicianPatientArbitrationAgreementDate: new Date().toISOString(),
  medicalServicesAgreementSign: undefined,
  medicalServicesAgreementDate: new Date().toISOString(),
  // Basic Details
  firstName: "",
  lastName: "",
  birthDate: "",
  gender: "",
  phoneNumber: "",
  email: "",
  address: null,
  city: "",
  state: "",
  zipCode: "",

  // Insurance Information
  hasInsurance: true,
  primaryInsurance: false,
  secondaryInsurance: false,
  patientInsurances: [INITIAL_INTAKE_INSURANCE],

  primary: {
    insuranceProvider: "",
    policyNumber: "",
    groupNumber: "",
    subscriberName: "",
    subscriberDOB: "",
    subscriberSSN: "",
    relationshipToPatient: "",
  },
  secondary: {
    insuranceProvider: "",
    policyNumber: "",
    groupNumber: "",
    subscriberName: "",
    subscriberDOB: "",
    subscriberSSN: "",
    relationshipToPatient: "",
  },

  // Medical History
  allergies: ALLERGIES.reduce(
    (acc, allergy) => ({ ...acc, [allergy.value]: false }),
    { none: false }
  ),
  allergiesNote: "",
  familyHistory: [{ problem: "", relative: "", age: 0 }],
  socialHistory: {
    alcohol: false,
    smoke: false,
    caffeine: false,
    sexuallyActive: false,
    stdCheck: false,
  },
  socialHistoryNote: "",
  surgicalHistory: [{ type: "", date: "", doctor: "", location: "" }],
  medicalHistory: MEDICAL_CONDITIONS.reduce(
    (acc, condition) => ({ ...acc, [condition.value]: false }),
    {}
  ),
  medicalHistoryNote: "",
  medications: [{ name: "", dosage: "", frequency: "", prescribedBy: "" }],

  // Individual Psychotherapy
  individualPsychotherapy: {
    primaryCarePhysicianServiceFocused: "",
    primaryDiagnosis: "",
    secondaryDiagnosis: "",
    primaryCarePhysician: "",
    currentMedications: "",
    allergies: "",
    relevantMedicalHistory: "",
    previousMentalHealthTreatment: "",
    psychiatricMedications: "",
    suicidalThoughtsHistory: "",
    substanceUseHistory: "",
    traumaHistory: "",
    currentCopingMechanisms: "",
    chiefConcern: "",
    onsetDate: "",
    areasOfConcern: {},
    previousTreatmentTherapy: "",
    treatmentGoals: "",
    birthHistory: "",
    developmentalMilestones: "",
    familyMedicalMentalHealthHistory: "",
    currentSupportSystem: "",
    educationLevel: "",
    occupation: "",
    employmentStatus: "",
    referralSource: "",
    insuranceProvider: "",
    policyMemberId: "",
  },

  // Consent and Agreements
  hippaConsent: false,
  medicalServicesAgreement: false,
  physicianPatientArbitrationAgreement: false,
  consent: false,

  // payment policy
  paymentPolicyDate: new Date(),
};

// intake form payment policy section
type PaymentPolicySection = {
  title: string;
  body?: string;
  bullets?: string[];
  footer?: string;
};
export const PAYMENT_POLICY_SECTIONS: PaymentPolicySection[] = [
  {
    title: "1. Insurance Information",
    body: "I understand that I am responsible for providing accurate and complete insurance information at the time of service. Failure to provide correct insurance details may result in claims denial, and I will be responsible for the full balance. I authorize the healthcare provider to release any information necessary to process my insurance claims.",
  },
  {
    title: "2. Financial Responsibility",
    body: "I understand that I am financially responsible for all charges related to services provided, including but not limited to:",
    bullets: [
      "Copayments",
      "Coinsurance",
      "Deductibles",
      "Non-covered services",
    ],
    footer:
      "Payment is expected at the time of service unless prior arrangements have been made.",
  },
  {
    title: "3. Insurance Claims",
    body: "As a courtesy, the practice may submit claims to my insurance provider. However:",
    bullets: [
      "Insurance coverage is not a guarantee of payment",
      "I am responsible for any balance not paid by my insurance",
      "If my insurance does not pay within a reasonable timeframe, I may be required to pay the outstanding balance",
    ],
  },
  {
    title: "4. Non-Covered Services",
    body: "I understand that some services may not be covered by my insurance plan. I agree to pay for any services deemed non-covered or not medically necessary by my insurance provider.",
  },
  {
    title: "5. Self-Pay Patients",
    body: "If I do not have insurance, I agree to pay for all services in full at the time of visit unless a payment plan has been agreed upon in advance.",
  },
  {
    title: "6. Payment Methods",
    body: "The practice accepts the following payment methods:",
    bullets: [
      "Credit/Debit Cards",
      "Cash",
      "Checks (subject to verification)",
      "Online payments (if applicable)",
    ],
  },
  {
    title: "7. Outstanding Balances",
    body: "I understand that any outstanding balances must be paid promptly. The practice reserves the right to:",
    bullets: [
      "Charge late fees (if applicable)",
      "Refer unpaid balances to a collection agency",
    ],
  },
  {
    title: "8. Missed Appointments / Cancellation Policy",
    body: "I understand that failure to cancel or reschedule an appointment within the required notice period may result in a cancellation or no-show fee.",
  },
  {
    title: "9. Refund Policy",
    body: "Overpayments or duplicate payments will be refunded after review and verification by the billing department.",
  },
  {
    title: "10. Consent for Communication",
    body: "I consent to receive billing-related communications via:",
    bullets: ["Phone calls", "Emails", "SMS/text messages"],
  },
  {
    title: "11. Assignment of Benefits",
    body: "I hereby authorize direct payment of my medical benefits to the provider for services rendered.",
  },
];
